import client, { unwrap } from "@/api/client";

// useQuery options that also keep the result in sessionStorage, so it survives a page reload for the
// rest of the browser tab's session. Storage can be full or blocked; then this is a plain query.
const PREFIX = "rq:";

const storageKey = (queryKey) => PREFIX + JSON.stringify(queryKey);

function readCached(queryKey, maxAgeMs) {
  try {
    const raw = sessionStorage.getItem(storageKey(queryKey));
    if (!raw) return undefined;
    const entry = JSON.parse(raw);
    return Date.now() - entry.savedAt < maxAgeMs ? entry : undefined;
  } catch {
    return undefined;
  }
}

/**
 * maxAgeMs bounds both the stored copy and the in-memory one. Keep it under the lifetime of anything
 * expiring inside the data, such as signed image URLs.
 */
export function sessionCachedQuery(queryKey, queryFn, { maxAgeMs, enabled = true }) {
  return {
    queryKey,
    enabled,
    queryFn: async () => {
      const data = await queryFn();
      try {
        sessionStorage.setItem(storageKey(queryKey), JSON.stringify({ data, savedAt: Date.now() }));
      } catch {
        // Quota exceeded or storage blocked: the in-memory cache still works.
      }
      return data;
    },
    initialData: () => readCached(queryKey, maxAgeMs)?.data,
    initialDataUpdatedAt: () => readCached(queryKey, maxAgeMs)?.savedAt,
    staleTime: maxAgeMs,
    gcTime: maxAgeMs,
  };
}

// Product image URLs are signed for 15 minutes (SIGNED_URL_TTL_SECONDS on the backend), so a cached
// list must be refetched well before then or its images stop loading.
const CATALOGUE_PRODUCTS_MAX_AGE_MS = 10 * 60_000;

/** A catalogue's products, shared by every page that lists them. */
export const catalogueProductsQuery = (catalogueId) =>
  sessionCachedQuery(
    ["catalogue-products", catalogueId],
    () => client.get(`/catalogues/${catalogueId}/products`).then(unwrap),
    { maxAgeMs: CATALOGUE_PRODUCTS_MAX_AGE_MS, enabled: !!catalogueId },
  );
